import { setLoader } from 'reducers/appConfigReducer';
import {
  setSelectedServiceAgreement,
  setSelectedServiceAgreementServiceRoles,
} from 'reducers/serviceAgreementsReducer';
import { moveForwardServiceAgreementProcessApi } from 'services/service-agreement/moveForwardServiceAgreementProcessApi';
import { getServiceAgreementApi } from 'services/service-agreement/getServiceAgreementApi';
import { callDeactivateServiceRole } from 'services/service-role/callDeactivateServiceRole';
import { callDeleteServiceRole } from 'services/service-role/callDeleteServiceRole';
import { callCreateServiceRole } from 'services/service-role/callCreateServiceRole';
import { AppDispatch, RootState } from 'store';
import { fetchServiceAgreement, getServiceAgreement } from './accountServiceAgreementsThunk';
import { closeModal, openModal } from 'reducers/modalReducer';
import { ServiceRole, StepMetadata } from 'pco-product-service-internal-consumer';
import { StepMetadataType } from 'types';

export const createServiceRole =
  (
    serviceRoleToCreate: Partial<ServiceRole>,
    serviceRoleUid?: string,
    setAdditionalDelegateModalOpen?: (open: boolean) => void,
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const selectedServiceAgreement = getState().serviceAgreements.selectedServiceAgreement;
    dispatch(
      setLoader({
        loading: true,
        message: 'Creating Service Role...',
      }),
    );

    if (serviceRoleUid) {
      await callDeleteServiceRole(serviceRoleUid).catch((error) => {
        dispatch(
          openModal({
            modalType: 'JBErrorModalWrapper',
            modalProps: {
              httpError: error,
              okHandler: () => {
                dispatch(closeModal());
              },
              technicalInfo: {
                title: 'Error when deleting service role',
                userErrorMessage: 'An error occurred when deleting service role. Please try again later.',
              },
            },
          }),
        );
        return;
      });
    }
    const createResponse = await callCreateServiceRole(serviceRoleToCreate).catch((error) => {
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when creating service role',
              userErrorMessage: 'An error occurred when creating service role. Please try again later.',
            },
          },
        }),
      );
      return;
    });

    const hasServiceRoleSelection =
      createResponse?.stepMetadata?.some(
        (step: StepMetadata) =>
          step.stepMetadataType === StepMetadataType.CUSTOM_COMPONENT &&
          step.customComponent === 'ServiceRoleSelection',
      ) ?? false;

    if (hasServiceRoleSelection && selectedServiceAgreement) {
      const taskId = createResponse?.stepMetadata?.filter(
        (step: StepMetadata) =>
          step.stepMetadataType === StepMetadataType.CUSTOM_COMPONENT &&
          step.customComponent === 'ServiceRoleSelection',
      )[0]?.taskId;

      await moveForwardServiceAgreementProcessApi(
        taskId ?? '',
        selectedServiceAgreement?.serviceAgreementId ?? '',
        undefined,
        undefined,
        undefined,
        undefined,
      ).catch((error) => {
        dispatch(
          openModal({
            modalType: 'JBErrorModalWrapper',
            modalProps: {
              httpError: error,
              okHandler: () => {
                dispatch(closeModal());
              },
              technicalInfo: {
                title: 'Error when creating service role',
                userErrorMessage: 'An error occurred when service role. Please try again later.',
              },
            },
          }),
        );
        return;
      });

      const updatedServiceAgreement = await getServiceAgreementApi(
        selectedServiceAgreement?.serviceAgreementId,
        null,
      ).catch((error) => {
        dispatch(
          openModal({
            modalType: 'JBErrorModalWrapper',
            modalProps: {
              httpError: error,
              okHandler: () => {
                dispatch(closeModal());
              },
              technicalInfo: {
                title: 'Error when retrieving a single Service Agreement',
                userErrorMessage:
                  'An error occurred when retrieving a single Service Agreement. Please try again later.',
              },
            },
          }),
        );
        return;
      });

      if (updatedServiceAgreement) {
        dispatch(getServiceAgreement(selectedServiceAgreement?.serviceAgreementId, null, false, false));
        dispatch(setSelectedServiceAgreementServiceRoles(updatedServiceAgreement.serviceRoles ?? []));
      }
    } else {
      if (createResponse?.serviceRoles) {
        dispatch(getServiceAgreement(selectedServiceAgreement?.serviceAgreementId, null, false, false));
        dispatch(setSelectedServiceAgreementServiceRoles(createResponse.serviceRoles));
      }
    }

    if (setAdditionalDelegateModalOpen) {
      setAdditionalDelegateModalOpen(false);
    }

    dispatch(
      setLoader({
        loading: false,
        message: '',
      }),
    );
  };

export const deleteServiceRole =
  (serviceRoleUid: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const selectedServiceAgreement = getState().serviceAgreements.selectedServiceAgreement;

    dispatch(
      setLoader({
        loading: true,
        message: 'Deleting Service Role...',
      }),
    );
    const deleteResponse = await callDeleteServiceRole(serviceRoleUid).catch((error) => {
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when deleting Service Role',
              userErrorMessage: 'An error occurred when deleting Service Role. Please try again later.',
            },
          },
        }),
      );
      return;
    });
    const serviceAgreement = await fetchServiceAgreement(selectedServiceAgreement?.serviceAgreementId, null, dispatch);
    dispatch(setSelectedServiceAgreement(serviceAgreement));
    dispatch(setSelectedServiceAgreementServiceRoles(deleteResponse?.serviceRoles ?? []));

    dispatch(
      setLoader({
        loading: false,
        message: '',
      }),
    );
  };

export const deactivateServiceRole =
  (serviceRoleUid: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const selectedServiceAgreement = getState().serviceAgreements.selectedServiceAgreement;

    dispatch(
      setLoader({
        loading: true,
        message: 'Deactivating Service Role...',
      }),
    );
    const deactivationResponse = await callDeactivateServiceRole(serviceRoleUid).catch((error) => {
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when deactivating Service Role.',
              userErrorMessage: 'An error occurred when deactivating Service Role. Please try again later.',
            },
          },
        }),
      );
      return;
    });
    const serviceAgreement = await fetchServiceAgreement(selectedServiceAgreement?.serviceAgreementId, null, dispatch);

    dispatch(setSelectedServiceAgreement(serviceAgreement));
    dispatch(setSelectedServiceAgreementServiceRoles(deactivationResponse?.serviceRoles ?? []));
    dispatch(
      setLoader({
        loading: false,
        message: '',
      }),
    );
  };
